import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Archive,
  Boxes,
  CalendarClock,
  Check,
  Copy,
  Database,
  HardDrive,
  FolderTree,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Server as ServerIcon,
  SlidersHorizontal,
  ShieldCheck,
  Square,
  Terminal,
  Trash2,
  Users,
} from "lucide-react";
import { api, del, patch, post, postForm, put, RequestError } from "../../api";
import {
  Config,
  EmptyServers,
  Field,
  formatBytes,
  Metric,
  PageHead,
  relative,
  ServerRow,
  State,
  stateLabel,
} from "../../components/common";
import type {
  AccessEntry,
  Backup,
  BackupPolicy,
  CrashDiagnostics,
  GameplaySettings,
  InstalledMod,
  Job,
  MetricPoint,
  ModPackage,
  ModConfigFile,
  ModConfigField,
  ModConfigRevision,
  ModPlan,
  ModUpdateAllPreview,
  ModUpdatePreview,
  ModProfileHistory,
  PlayerSession,
  Schedule,
  Server,
  World,
  Role,
} from "../../types";
import { FileManager } from "./FileManager";
import { ConsoleView } from "./ConsoleView";
import { AdminBridgeCard } from "./AdminBridgeCard";
import { ItemProvenanceCard } from "./ItemProvenanceCard";
import { CharacterAdministration } from "./CharacterAdministration";
import { Avatar, CardSkeleton, EmptyState, useActionDialog } from "../../components/ui";

interface ServerSettingsDraft {
  displayName: string;
  newPassword: string;
  public: boolean;
  crossplay: boolean;
  autostart: boolean;
  port: string;
  saveInterval: string;
  backups: string;
  backupShort: string;
  backupLong: string;
  steamBranch: string;
  memoryHigh: string;
  memoryMax: string;
  cpuQuota: string;
  nice: string;
}

export function ServersView({
  onCreate,
  onSelect,
}: {
  onCreate: () => void;
  onSelect: (s: Server) => void;
}) {
  const [servers, setServers] = useState<Server[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api<Server[]>("/servers").then(setServers).finally(() => setLoading(false));
  }, []);
  const shown = servers.filter((s) =>
    (s.displayName + s.activeWorldName).toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <>
      <PageHead
        eyebrow="SEUS SALÕES"
        title="Servidores"
        description="Cada servidor pode ficar ligado ou desligado sem apagar seus mundos."
      >
        <button className="primary" onClick={onCreate}>
          <Plus size={17} />
          Novo servidor
        </button>
      </PageHead>
      <div className="toolbar">
        <div className="search">
          <Search size={17} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar servidor ou mundo…"
          />
        </div>
        <span>
          {shown.length} servidor{shown.length !== 1 && "es"}
        </span>
      </div>
      <section className="panel">
        <div className="server-list roomy">
          {loading ? <CardSkeleton rows={3}/> : shown.map((s) => (
            <ServerRow key={s.id} server={s} onClick={() => onSelect(s)} />
          ))}
          {!loading && !shown.length && <EmptyServers onCreate={onCreate} />}
        </div>
      </section>
    </>
  );
}

export function ServerDetail({
  initial,
  canManage,
  onBack,
  fileRole,
}: {
  initial: Server;
  canManage: boolean;
  onBack: () => void;
  fileRole?: Role;
}) {
  const { confirm: confirmAction, prompt: promptAction, host: dialogHost } = useActionDialog();
  const [s, setServer] = useState(initial);
  const [serverDraft, setServerDraft] = useState<ServerSettingsDraft>(() =>
    draftFromServer(initial),
  );
  const [worlds, setWorlds] = useState<World[]>([]);
  const [gameplay, setGameplay] = useState<GameplaySettings | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [backupPolicy, setBackupPolicy] = useState<BackupPolicy | null>(null);
  const [diagnostics, setDiagnostics] = useState<CrashDiagnostics | null>(null);
  const [showCrashLogs, setShowCrashLogs] = useState(false);
  const [metrics, setMetrics] = useState<MetricPoint[]>([]);
  const [players, setPlayers] = useState<PlayerSession[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [access, setAccess] = useState<AccessEntry[]>([]);
  const [installedMods, setInstalledMods] = useState<InstalledMod[]>([]);
  const [modQuery, setModQuery] = useState("");
  const [modResults, setModResults] = useState<ModPackage[]>([]);
  const [modSelection, setModSelection] = useState<ModPackage | null>(null);
  const [modPlan, setModPlan] = useState<ModPlan | null>(null);
  const [modUpdateAll, setModUpdateAll] = useState<ModUpdateAllPreview | null>(null);
  const [modUpdate, setModUpdate] = useState<{ item: InstalledMod; preview: ModUpdatePreview } | null>(null);
  const [modProfiles, setModProfiles] = useState<ModProfileHistory[]>([]);
  const [modConfigs, setModConfigs] = useState<ModConfigFile[]>([]);
  const [selectedModConfig, setSelectedModConfig] = useState<ModConfigFile | null>(null);
  const [modConfigRevisions, setModConfigRevisions] = useState<ModConfigRevision[]>([]);
  const [job, setJob] = useState<Job | null>(null);
  const [lastFailure, setLastFailure] = useState<Job | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [newWorld, setNewWorld] = useState("");
  const [worldImportFiles, setWorldImportFiles] = useState<File[]>([]);
  const [worldImportName, setWorldImportName] = useState("");
  const [worldImportActivate, setWorldImportActivate] = useState(false);
  const [worldImportConflict, setWorldImportConflict] = useState("cancel");
  const [worldImportInputKey, setWorldImportInputKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteBackupsAcknowledged, setDeleteBackupsAcknowledged] = useState(false);
  const [ecoStop, setEcoStop] = useState(initial.ecoStopMinutes);
  const [accessDraft, setAccessDraft] = useState<{
    type: AccessEntry["type"];
    platformId: string;
    note: string;
  }>({ type: "admin", platformId: "", note: "" });
  const reload = useCallback(async () => {
    const [next, w, g, b, m, p, sc, ac, installed, profiles, jobs] = await Promise.all([
      api<Server>(`/servers/${initial.id}`),
      api<World[]>(`/servers/${initial.id}/worlds`),
      api<GameplaySettings>(`/servers/${initial.id}/gameplay`),
      api<Backup[]>(`/servers/${initial.id}/backups`),
      api<MetricPoint[]>(`/servers/${initial.id}/metrics`),
      api<PlayerSession[]>(`/servers/${initial.id}/players`),
      api<Schedule[]>(`/servers/${initial.id}/schedules`),
      api<AccessEntry[]>(`/servers/${initial.id}/access`),
      api<InstalledMod[]>(`/servers/${initial.id}/mods`),
      api<ModProfileHistory[]>(`/servers/${initial.id}/mods/profiles`),
      api<Job[]>("/jobs?limit=100"),
    ]);
    const serverJobs = jobs.filter((x) => x.serverId === initial.id);
    const latestJob = serverJobs[0];
    const lifecycleFailureRecovered =
      latestJob?.status === "failed" &&
      ((next.state === "ONLINE" &&
        ["start", "restart", "graceful-restart"].includes(latestJob.kind)) ||
        (next.state === "OFFLINE" && latestJob.kind === "stop"));
    setServer(next);
    setWorlds(w);
    setGameplay((current) => (current?.worldId === g.worldId ? current : g));
    setBackups(b);
    setMetrics(m);
    setPlayers(p);
    setSchedules(sc);
    setAccess(ac);
    setInstalledMods(Array.isArray(installed) ? installed : []);
    setModProfiles(Array.isArray(profiles) ? profiles : []);
    setJob(
      serverJobs.find((x) => x.status === "queued" || x.status === "running") ??
        null,
    );
    setLastFailure(
      latestJob?.status === "failed" && !lifecycleFailureRecovered
        ? latestJob
        : null,
    );
  }, [initial.id]);
  useEffect(() => {
    void reload();
    const id = setInterval(reload, 3000);
    return () => clearInterval(id);
  }, [reload]);
  useEffect(() => {
    void api<BackupPolicy>(`/servers/${initial.id}/backup-policy`).then(
      setBackupPolicy,
    );
  }, [initial.id]);
  useEffect(() => {
    if (s.state !== "CRASHED" && s.state !== "CRASH_LOOP") {
      setDiagnostics(null);
      setShowCrashLogs(false);
      return;
    }
    void api<CrashDiagnostics>(`/servers/${initial.id}/diagnostics`).then(
      setDiagnostics,
      () => setDiagnostics(null),
    );
  }, [initial.id, s.state]);
  const action = async (name: string) => {
    setBusy(name);
    setError("");
    try {
      await post(`/servers/${s.id}/actions/${name}`, {});
      await reload();
    } catch (e) {
      setError(
        e instanceof RequestError
          ? e.message
          : "Não foi possível concluir a solicitação.",
      );
    } finally {
      setBusy("");
    }
  };
  const addWorld = async () => {
    if (!newWorld) return;
    setBusy("world");
    setError("");
    try {
      await post(`/servers/${s.id}/worlds`, {
        name: newWorld,
        activate: false,
      });
      setNewWorld("");
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível criar o mundo.",
      );
    } finally {
      setBusy("");
    }
  };
  const importWorld = async () => {
    const db = worldImportFiles.find((file) => file.name.endsWith(".db"));
    const fwl = worldImportFiles.find((file) => file.name.endsWith(".fwl"));
    const dbBase = db?.name.slice(0, -3);
    const fwlBase = fwl?.name.slice(0, -4);
    if (
      worldImportFiles.length !== 2 ||
      !db ||
      !fwl ||
      !dbBase ||
      dbBase !== fwlBase
    ) {
      setError("Selecione juntos um arquivo .db e um .fwl com o mesmo nome base.");
      return;
    }
    setBusy("world-import");
    setError("");
    try {
      const form = new FormData();
      form.append("db", db);
      form.append("fwl", fwl);
      if (worldImportName.trim()) form.append("name", worldImportName.trim());
      form.append("activate", String(worldImportActivate));
      form.append("onConflict", worldImportConflict);
      await postForm(`/servers/${s.id}/worlds/import`, form);
      setWorldImportFiles([]);
      setWorldImportName("");
      setWorldImportActivate(false);
      setWorldImportConflict("cancel");
      setWorldImportInputKey((value) => value + 1);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível importar o mundo.",
      );
    } finally {
      setBusy("");
    }
  };
  const activate = async (world: World) => {
    setBusy("world-switch");
    setError("");
    try {
      await post(`/servers/${s.id}/worlds/${world.id}/activate`, {});
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível trocar o mundo.",
      );
    } finally {
      setBusy("");
    }
  };
  const cloneWorld = async (world: World) => {
    const name = await promptAction({ title: "Duplicar mundo", description: "A cópia preserva o mundo original e cria um novo conjunto de arquivos.", confirmLabel: "Criar cópia", input: { label: "Nome da cópia", defaultValue: `${world.name}-copia` } });
    if (!name) return;
    setBusy("world-clone");
    setError("");
    try {
      await post(`/servers/${s.id}/worlds/${world.id}/clone`, { name });
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível duplicar o mundo.",
      );
    } finally {
      setBusy("");
    }
  };
  const renameWorld = async (world: World) => {
    const name = await promptAction({ title: "Renomear mundo", description: "O painel atualizará os arquivos relacionados de forma segura.", confirmLabel: "Renomear", input: { label: "Novo nome do mundo", defaultValue: world.name } });
    if (!name || name === world.name) return;
    setBusy("world-rename");
    setError("");
    try {
      await patch(`/servers/${s.id}/worlds/${world.id}`, { name });
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível renomear o mundo.",
      );
    } finally {
      setBusy("");
    }
  };
  const archiveWorld = async (world: World) => {
    setBusy("world-archive");
    setError("");
    try {
      await patch(`/servers/${s.id}/worlds/${world.id}`, {
        archived: !world.archived,
      });
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível alterar o arquivamento.",
      );
    } finally {
      setBusy("");
    }
  };
  const deleteWorld = async (world: World) => {
    if (!await confirmAction({ title: `Excluir ${world.name}?`, description: "Um backup será criado antes da exclusão. Esta ação remove os arquivos atuais do mundo.", confirmLabel: "Excluir mundo", tone: "danger" })) return;
    setBusy("world-delete");
    setError("");
    try {
      await del(`/servers/${s.id}/worlds/${world.id}`);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível iniciar a exclusão segura.",
      );
    } finally {
      setBusy("");
    }
  };
  const restore = async (backup: Backup) => {
    if (!await confirmAction({ title: "Restaurar este backup?", description: `Este backup é de ${relative(backup.createdAt)}. O estado atual será preservado em uma cópia antes da restauração.`, confirmLabel: "Restaurar backup" })) return;
    setBusy("restore");
    setError("");
    try {
      await post(`/servers/${s.id}/backups/${backup.id}/restore`, {});
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível restaurar o backup.",
      );
    } finally {
      setBusy("");
    }
  };
  const removeBackup = async (backup: Backup) => {
    if (!await confirmAction({ title: "Excluir backup permanentemente?", description: "Este ponto de restauração não poderá ser recuperado depois da exclusão.", confirmLabel: "Excluir backup", tone: "danger" })) return;
    setBusy("delete-backup");
    setError("");
    try {
      await del(`/servers/${s.id}/backups/${backup.id}`);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível apagar o backup.",
      );
    } finally {
      setBusy("");
    }
  };
  const saveBackupPolicy = async () => {
    if (!backupPolicy) return;
    setBusy("backup-policy");
    setError("");
    try {
      setBackupPolicy(
        await patch<BackupPolicy>(
          `/servers/${s.id}/backup-policy`,
          backupPolicy,
        ),
      );
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível salvar a retenção de backups.",
      );
    } finally {
      setBusy("");
    }
  };
  const searchMods = async () => {
    if (!modQuery.trim()) return;
    setBusy("mod-search");
    setError("");
    try {
      setModResults(
        await api<ModPackage[]>(
          `/mods/search?q=${encodeURIComponent(modQuery.trim())}&limit=12`,
        ),
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível pesquisar mods.",
      );
    } finally {
      setBusy("");
    }
  };
  const previewMod = async (item: ModPackage) => {
    setBusy("mod-preview");
    setError("");
    try {
      const plan = await post<ModPlan>(`/servers/${s.id}/mods/preview`, {
        namespace: item.id.namespace,
        name: item.id.name,
        version: item.latestVersion,
      });
      setModSelection(item);
      setModPlan(plan);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível preparar as dependências.",
      );
    } finally {
      setBusy("");
    }
  };
  const installModPlan = async () => {
    if (!modSelection) return;
    setBusy("mod-install");
    setError("");
    try {
      await post(`/servers/${s.id}/mods/install`, {
        namespace: modSelection.id.namespace,
        name: modSelection.id.name,
        version: modSelection.latestVersion,
      });
      setModPlan(null);
      setModSelection(null);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível instalar os mods.",
      );
    } finally {
      setBusy("");
    }
  };
  const setModPinned = async (item: InstalledMod) => {
    const key = `${item.namespace}-${item.name}`;
    setBusy(`mod-pin-${key}`);
    setError("");
    try {
      await patch<InstalledMod>(
        `/servers/${s.id}/mods/${encodeURIComponent(item.namespace)}/${encodeURIComponent(item.name)}`,
        { pinned: !item.pinned },
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível proteger esta versão.");
    } finally {
      setBusy("");
    }
  };
  const previewInstalledModUpdate = async (item: InstalledMod) => {
    const key = `${item.namespace}-${item.name}`;
    setBusy(`mod-update-preview-${key}`);
    setError("");
    try {
      const preview = await post<ModUpdatePreview>(
        `/servers/${s.id}/mods/${encodeURIComponent(item.namespace)}/${encodeURIComponent(item.name)}/update/preview`,
        {},
      );
      setModUpdate({ item, preview });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível verificar a atualização deste mod.");
    } finally {
      setBusy("");
    }
  };
  const applyInstalledModUpdate = async () => {
    if (!modUpdate) return;
    const { item } = modUpdate;
    const key = `${item.namespace}-${item.name}`;
    setBusy(`mod-update-${key}`);
    setError("");
    try {
      await post(
        `/servers/${s.id}/mods/${encodeURIComponent(item.namespace)}/${encodeURIComponent(item.name)}/update`,
        {},
      );
      setModUpdate(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível atualizar este mod.");
    } finally {
      setBusy("");
    }
  };
  const previewAllModUpdates = async () => {
    setBusy("mod-update-all-preview");
    setError("");
    try {
      setModUpdateAll(await post<ModUpdateAllPreview>(`/servers/${s.id}/mods/update-all/preview`, {}));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível verificar as atualizações dos mods.");
    } finally {
      setBusy("");
    }
  };
  const applyAllModUpdates = async () => {
    setBusy("mod-update-all");
    setError("");
    try {
      await post(`/servers/${s.id}/mods/update-all`, {});
      setModUpdateAll(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível atualizar os mods.");
    } finally {
      setBusy("");
    }
  };
  const setModEnabled = async (item: InstalledMod) => {
    const key = `${item.namespace}-${item.name}`;
    setBusy(`mod-enable-${key}`);
    setError("");
    try {
      await patch(
        `/servers/${s.id}/mods/${encodeURIComponent(item.namespace)}/${encodeURIComponent(item.name)}`,
        { enabled: !item.enabled },
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível alterar este mod.");
    } finally {
      setBusy("");
    }
  };
  const removeInstalledMod = async (item: InstalledMod) => {
    if (!await confirmAction({ title: `Remover ${item.name}?`, description: "Um backup será criado antes da alteração e as dependências serão recalculadas.", confirmLabel: "Remover mod", tone: "danger" })) return;
    const key = `${item.namespace}-${item.name}`;
    setBusy(`mod-remove-${key}`);
    setError("");
    try {
      await del(
        `/servers/${s.id}/mods/${encodeURIComponent(item.namespace)}/${encodeURIComponent(item.name)}`,
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível remover este mod.");
    } finally {
      setBusy("");
    }
  };
  const restoreModProfile = async (profile: ModProfileHistory) => {
    if (!await confirmAction({ title: "Restaurar perfil de mods?", description: `O perfil contém ${profile.packages} componentes. O estado atual será protegido antes da mudança.`, confirmLabel: "Restaurar perfil" })) return;
    setBusy(`mod-profile-${profile.id}`);
    setError("");
    try {
      await post(`/servers/${s.id}/mods/profiles/${profile.id}/restore`, {});
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível restaurar o perfil.");
    } finally {
      setBusy("");
    }
  };
  const loadModConfigs = async () => {
    setBusy("mod-config-list");
    setError("");
    try {
      setModConfigs(await api<ModConfigFile[]>(`/servers/${s.id}/mods/configs`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível listar as configurações.");
    } finally {
      setBusy("");
    }
  };
  const openModConfig = async (item: ModConfigFile) => {
    setBusy(`mod-config-${item.name}`);
    setError("");
    try {
      const [config, revisions] = await Promise.all([
        api<ModConfigFile>(`/servers/${s.id}/mods/configs/${encodeURIComponent(item.name)}`),
        api<ModConfigRevision[]>(`/servers/${s.id}/mods/configs/${encodeURIComponent(item.name)}/revisions`),
      ]);
      setSelectedModConfig(config);
      setModConfigRevisions(revisions);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível abrir a configuração.");
    } finally {
      setBusy("");
    }
  };
  const saveModConfig = async () => {
    if (!selectedModConfig) return;
    setBusy(`mod-config-save-${selectedModConfig.name}`);
    setError("");
    try {
      const saved = await put<ModConfigFile>(
        `/servers/${s.id}/mods/configs/${encodeURIComponent(selectedModConfig.name)}`,
        { content: selectedModConfig.content ?? "" },
      );
      setSelectedModConfig(saved);
      setModConfigRevisions(await api<ModConfigRevision[]>(`/servers/${s.id}/mods/configs/${encodeURIComponent(saved.name)}/revisions`));
      await loadModConfigs();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar a configuração.");
    } finally {
      setBusy("");
    }
  };
  const updateModConfigField = (field: ModConfigField, value: string) => {
    if (!selectedModConfig) return;
    const lines = (selectedModConfig.content ?? "").split("\n");
    const line = lines[field.line] ?? "";
    const separator = line.indexOf("=");
    if (separator < 0) return;
    lines[field.line] = `${line.slice(0, separator + 1)} ${value}`;
    setSelectedModConfig({
      ...selectedModConfig,
      content: lines.join("\n"),
      fields: (selectedModConfig.fields ?? []).map((item) => item.line === field.line ? { ...item, value } : item),
    });
  };
  const restoreModConfig = async (revision: ModConfigRevision) => {
    if (!selectedModConfig || !await confirmAction({ title: "Restaurar configuração?", description: "A versão atual também será preservada no histórico antes da restauração.", confirmLabel: "Restaurar versão" })) return;
    setBusy(`mod-config-restore-${revision.id}`);
    setError("");
    try {
      const restored = await post<ModConfigFile>(
        `/servers/${s.id}/mods/configs/${encodeURIComponent(selectedModConfig.name)}/revisions/${revision.id}/restore`,
        {},
      );
      setSelectedModConfig(restored);
      setModConfigRevisions(await api<ModConfigRevision[]>(`/servers/${s.id}/mods/configs/${encodeURIComponent(restored.name)}/revisions`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível restaurar a configuração.");
    } finally {
      setBusy("");
    }
  };
  const saveEconomy = async () => {
    setBusy("settings");
    setError("");
    try {
      const next = await patch<Server>(`/servers/${s.id}/settings`, {
        ecoStopMinutes: ecoStop,
        updatePolicy: s.updatePolicy,
        autostart: s.autostart,
      });
      setServer(next);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível salvar a economia de recursos.",
      );
    } finally {
      setBusy("");
    }
  };
  const saveUpdatePolicy = async (policy: string) => {
    setBusy("settings");
    setError("");
    try {
      const next = await patch<Server>(`/servers/${s.id}/settings`, {
        ecoStopMinutes: s.ecoStopMinutes,
        updatePolicy: policy,
        autostart: s.autostart,
      });
      setServer(next);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível salvar a política de atualização.",
      );
    } finally {
      setBusy("");
    }
  };
  const addSchedule = async (template: string) => {
    setBusy("schedule");
    setError("");
    try {
      await post(`/servers/${s.id}/schedules`, {
        template,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      });
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível criar a automação.",
      );
    } finally {
      setBusy("");
    }
  };
  const removeSchedule = async (id: string) => {
    setBusy("schedule");
    setError("");
    try {
      await del(`/servers/${s.id}/schedules/${id}`);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível remover a automação.",
      );
    } finally {
      setBusy("");
    }
  };
  const addAccess = async () => {
    if (!accessDraft.platformId) return;
    setBusy("access");
    setError("");
    try {
      await post(`/servers/${s.id}/access`, accessDraft);
      setAccessDraft({ ...accessDraft, platformId: "", note: "" });
      await reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível atualizar a lista.",
      );
    } finally {
      setBusy("");
    }
  };
  const removeAccess = async (item: AccessEntry) => {
    setBusy("access");
    setError("");
    try {
      await del(
        `/servers/${s.id}/access/${item.type}/${encodeURIComponent(item.platformId)}`,
      );
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível remover a pessoa da lista.",
      );
    } finally {
      setBusy("");
    }
  };
  const setModifier = (name: string, value: string) =>
    setGameplay((g) =>
      g ? { ...g, modifiers: { ...g.modifiers, [name]: value } } : g,
    );
  const toggleWorldKey = (key: string) =>
    setGameplay((g) =>
      g
        ? {
            ...g,
            worldKeys: g.worldKeys.includes(key)
              ? g.worldKeys.filter((x) => x !== key)
              : [...g.worldKeys, key],
          }
        : g,
    );
  const saveGameplay = async (restart: boolean) => {
    if (!gameplay) return;
    setBusy("gameplay");
    setError("");
    try {
      const saved = await patch<GameplaySettings>(`/servers/${s.id}/gameplay`, {
        preset: gameplay.preset,
        modifiers: gameplay.modifiers,
        worldKeys: gameplay.worldKeys,
      });
      setGameplay(saved);
      if (restart && s.state === "ONLINE")
        await post(`/servers/${s.id}/actions/restart`, {});
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível salvar as opções de gameplay.",
      );
    } finally {
      setBusy("");
    }
  };
  const saveServerSettings = async (restart: boolean) => {
    setBusy("server-settings");
    setError("");
    try {
      const next = await patch<Server>(`/servers/${s.id}/settings`, {
        displayName: serverDraft.displayName,
        newPassword: serverDraft.newPassword,
        public: serverDraft.public,
        crossplay: serverDraft.crossplay,
        autostart: serverDraft.autostart,
        port: Number(serverDraft.port),
        saveInterval: Number(serverDraft.saveInterval),
        backups: Number(serverDraft.backups),
        backupShort: Number(serverDraft.backupShort),
        backupLong: Number(serverDraft.backupLong),
        steamBranch: serverDraft.steamBranch,
        memoryHigh: Number(serverDraft.memoryHigh),
        memoryMax: Number(serverDraft.memoryMax),
        cpuQuota: Number(serverDraft.cpuQuota),
        nice: Number(serverDraft.nice),
      });
      setServer(next);
      setServerDraft(draftFromServer(next));
      if (restart && s.state === "ONLINE")
        await post(`/servers/${s.id}/actions/restart`, {});
      await reload();
    } catch (e) {
      setError(
        e instanceof RequestError
          ? e.message
          : "Não foi possível salvar as configurações do servidor.",
      );
    } finally {
      setBusy("");
    }
  };
  const deleteServer = async () => {
    if (deleteConfirmName !== s.displayName || !deleteBackupsAcknowledged) return;
    setBusy("server-delete");
    setError("");
    try {
      await del(`/servers/${s.id}`, {
        confirmName: deleteConfirmName,
        deleteBackups: true,
      });
      onBack();
    } catch (e) {
      setError(
        e instanceof RequestError
          ? e.message
          : "Não foi possível excluir o servidor.",
      );
      setBusy("");
    }
  };
  const address = s.joinAddress;
  const blocked = s.state === "INSTALLING" || Boolean(job) || Boolean(busy);
  const currentMetric = metrics[0];
  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const sections = [
    { label: "Visão geral", selector: ".metric-grid.compact", icon: Activity },
    { label: "Jogadores", selector: ".character-admin", icon: Users },
    { label: "Console", selector: ".console-panel", icon: Terminal },
    { label: "Mundos e backups", selector: ".world-list", icon: Database },
    { label: "Mods", selector: ".mods-panel", icon: Boxes },
    { label: "Gameplay", selector: ".gameplay-panel", icon: SlidersHorizontal },
    { label: "Automação", selector: ".schedule-list", icon: CalendarClock },
    { label: "Ajustes do servidor", selector: ".server-settings-panel", icon: ShieldCheck },
    { label: "Arquivos", selector: ".file-manager", icon: FolderTree },
  ];
  const jumpTo = (selector: string) => document.querySelector(selector)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <>
      <button className="back-link" onClick={onBack}>
        ← Todos os servidores
      </button>
      <div className="server-hero">
        <div>
          <div className="title-state">
            <State state={s.state} />
          </div>
          <h1>{s.displayName}</h1>
          <p>
            Mundo <b>{s.activeWorldName || "não selecionado"}</b> · Portas UDP{" "}
            {s.port} e {s.port + 1} · {s.crossplay ? "Crossplay" : "Steam"}
          </p>
        </div>
        <div className="action-group">
          {s.state === "ONLINE" ? (
            <>
              <button
                className="secondary"
                disabled={blocked}
                onClick={() => void action("restart")}
              >
                <RotateCcw size={16} />
                Reiniciar
              </button>
              <button
                className="secondary"
                disabled={blocked}
                onClick={() => void action("stop")}
              >
                <Square size={15} />
                Desligar
              </button>
            </>
          ) : (
            <button
              className="primary"
              disabled={blocked}
              onClick={() => void action("start")}
            >
              <Play size={16} />
              Ligar
            </button>
          )}
          <button
            className="secondary"
            disabled={blocked || s.state === "INSTALLING"}
            onClick={() => void action("backup")}
          >
            <Archive size={16} />
            Criar backup
          </button>
        </div>
      </div>
      <nav className="server-section-nav" aria-label="Áreas do servidor">
        {sections.map(({label,selector,icon:Icon})=><button type="button" key={label} onClick={()=>jumpTo(selector)}><Icon size={15}/>{label}</button>)}
      </nav>
      {(s.state === "INSTALLING" || job) && (
        <div className="operation-card">
          <div>
            <RefreshCw className="spin" size={20} />
            <span>
              <b>{job?.message || "Preparando o servidor"}</b>
              <small>
                Você pode sair desta tela. A atividade continua no host.
              </small>
            </span>
          </div>
          <div className="progress">
            <i style={{ width: `${job?.progress ?? 5}%` }} />
          </div>
        </div>
      )}
      {!job && (s.state === "CRASHED" || s.state === "CRASH_LOOP") && (
        <section className="crash-card">
          <div>
            <span className="eyebrow">RECUPERAÇÃO</span>
            <h2>O servidor não conseguiu iniciar</h2>
            <p>
              Estas são pistas, não uma causa confirmada. Seus mundos não são
              alterados pelo diagnóstico.
            </p>
          </div>
          {diagnostics ? (
            <div className="diagnostic-clues">
              {diagnostics.clues.map((clue) => (
                <div key={clue.kind}>
                  <b>{clue.title}</b>
                  <small>{clue.evidence}</small>
                </div>
              ))}
            </div>
          ) : (
            <small>Reunindo os sinais disponíveis…</small>
          )}
          <div className="inline-actions">
            <button
              className="secondary small"
              disabled={Boolean(busy)}
              onClick={() => void action("start")}
            >
              Tentar novamente
            </button>
            <button
              className="secondary small"
              disabled={Boolean(busy)}
              onClick={() => void action("validate")}
            >
              Verificar arquivos
            </button>
            <button
              className="secondary small"
              disabled={!diagnostics}
              onClick={() => setShowCrashLogs((value) => !value)}
            >
              {showCrashLogs ? "Ocultar logs" : "Ver logs"}
            </button>
            {diagnostics && diagnostics.installedMods.length > 0 && (
              <button
                className="secondary small"
                disabled={Boolean(busy)}
                onClick={() => void action("test-without-mods")}
              >
                Testar sem mods
              </button>
            )}
          </div>
          {showCrashLogs && diagnostics && (
            <div className="diagnostic-details">
              <p>
                Saída {diagnostics.exitCode} · {diagnostics.restartCount}
                {" "}reinícios · pico de memória {formatBytes(diagnostics.memoryPeak)}
              </p>
              <p>
                Valheim {diagnostics.valheimBuild || "sem build identificada"} · {diagnostics.installedMods.length} {diagnostics.installedMods.length === 1 ? "mod ativo" : "mods ativos"}
                {diagnostics.lastUpdateAt > 0 && ` · última atualização ${relative(diagnostics.lastUpdateAt)}`}
              </p>
              <pre>{diagnostics.logLines.join("\n") || "Nenhum log disponível."}</pre>
            </div>
          )}
        </section>
      )}
      {error && <div className="notice error">{error}</div>}
      {!job && lastFailure && (
        <div className="notice error">
          <b>
            {lastFailure.message ||
              "Não foi possível concluir a última operação."}
          </b>
          <span> Servidor: {stateLabel(s.state)}.</span>
          <div className="inline-actions">
            {["start", "check-update", "backup", "update", "validate"].includes(
              lastFailure.kind,
            ) && (
              <button
                className="secondary small"
                disabled={Boolean(busy)}
                onClick={() => void action(lastFailure.kind)}
              >
                Tentar novamente
              </button>
            )}
            {lastFailure.kind === "update" && (
              <button
                className="secondary small"
                disabled={Boolean(busy)}
                onClick={() => void action("validate")}
              >
                Verificar arquivos
              </button>
            )}
            <details>
              <summary>Ver detalhes</summary>
              <code>{lastFailure.details || lastFailure.errorCode}</code>
            </details>
          </div>
        </div>
      )}
      <div className="metric-grid compact">
        <Metric
          label="Estado"
          value={stateLabel(s.state)}
          sub={s.stateReason || "informado pelo agente local"}
          icon={Activity}
          tone={s.state === "ONLINE" ? "green" : ""}
        />
        <Metric
          label="Jogadores"
          value={
            currentMetric?.players == null
              ? "Indisponível"
              : String(currentMetric.players)
          }
          sub={
            currentMetric?.players == null
              ? "aguardando leitura confiável do Valheim"
              : "online na última observação"
          }
          icon={ServerIcon}
        />
        <Metric
          label="Memória do serviço"
          value={formatBytes(currentMetric?.cgroupMemoryBytes ?? currentMetric?.rssBytes)}
          sub={
            currentMetric?.cpu == null
              ? "Cgroup · CPU indisponível"
              : `Cgroup · RSS do processo ${formatBytes(currentMetric.processRssBytes)} · CPU ${currentMetric.cpu.toFixed(1)}% (100% = 1 core)`
          }
          icon={Boxes}
        />
        <Metric
          label="Build instalada"
          value={s.installedBuild || "Preparando"}
          sub={`canal ${s.steamBranch}`}
          icon={HardDrive}
        />
      </div>
      <ConsoleView serverId={s.id} role={fileRole}/>
      <AdminBridgeCard serverId={s.id} role={fileRole ?? (canManage ? "administrator" : "viewer")} />
      <ItemProvenanceCard serverId={s.id} role={fileRole ?? (canManage ? "administrator" : "viewer")} />
      <CharacterAdministration serverId={s.id} players={players} role={fileRole ?? (canManage ? "administrator" : "viewer")} onChanged={reload} />
      <div className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">COMO ENTRAR</span>
              <h2>
                {s.crossplay ? "Código para entrar" : "Endereço do servidor"}
              </h2>
            </div>
            <ServerIcon size={20} />
          </div>
          {s.crossplay && s.joinCode ? (
            <>
              <p>Compartilhe este código e a senha com seus jogadores.</p>
              <button
                className="copy-address"
                onClick={() => void copy(s.joinCode!)}
              >
                <code>{s.joinCode}</code>
                <span>
                  <Copy size={16} />
                  Copiar
                </span>
              </button>
            </>
          ) : s.crossplay ? (
            <p>
              O código desta sessão aparecerá aqui assim que o relay confirmar o
              servidor.
            </p>
          ) : null}
          {address ? (
            <>
              <p>Endereço público:</p>
              <button
                className="copy-address"
                onClick={() => void copy(address)}
              >
                <code>{address}</code>
                <span>
                  {copied ? (
                    <>
                      <Check size={16} />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copiar
                    </>
                  )}
                </span>
              </button>
            </>
          ) : !s.crossplay ? (
            <div className="notice">
              Confirme o endereço público deste host em Configurações antes de
              compartilhar.
            </div>
          ) : null}
          <small className="field-hint">
            {s.crossplay
              ? "Crossplay usa relay e normalmente não requer encaminhamento de portas."
              : s.state === "ONLINE" && s.udpListening
                ? "Servidor ouvindo localmente nas duas portas UDP. Acesso externo não verificado."
                : "Acesso externo não verificado."}
          </small>
        </section>
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">CENTRAL DE ATUALIZAÇÕES</span>
              <h2>
                {s.updateAvailable
                  ? "Nova versão disponível"
                  : "Valheim Dedicated Server"}
              </h2>
            </div>
            <ShieldCheck size={20} />
          </div>
          <p>
            {s.updateAvailable
              ? `Build ${s.latestBuild} pronta. Um backup será criado depois de salvar e desligar o servidor.`
              : s.buildCheckedAt
                ? `Build ${s.installedBuild || "—"} verificada ${relative(s.buildCheckedAt)}.`
                : "A versão ainda não foi consultada."}
          </p>
          <div className="inline-actions">
            <button
              className="secondary small"
              disabled={blocked}
              onClick={() => void action("check-update")}
            >
              <RefreshCw size={15} />
              Buscar atualização
            </button>
            {s.updateAvailable && (
              <button
                className="primary small"
                disabled={blocked}
                onClick={() => void action("update")}
              >
                Atualizar com backup
              </button>
            )}
            <button
              className="text-button"
              disabled={blocked}
              onClick={() => void action("validate")}
            >
              Verificar integridade
            </button>
          </div>
          <label className="check update-check">
            <input
              type="checkbox"
              checked={s.updatePolicy === "auto-empty"}
              disabled={blocked}
              onChange={(e) =>
                void saveUpdatePolicy(
                  e.target.checked ? "auto-empty" : "notify",
                )
              }
            />
            <i />
            <span>
              <b>Atualizar automaticamente quando vazio</b>
              <small>
                O padrão é apenas avisar. O painel só interrompe um servidor
                após confirmar zero jogadores.
              </small>
            </span>
          </label>
        </section>
      </div>
      <div className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">MUNDOS</span>
              <h2>Armazenados neste servidor</h2>
            </div>
          </div>
          <div className="inline-create">
            <Field
              required={false}
              label="Novo mundo"
              value={newWorld}
              onChange={setNewWorld}
              placeholder="Meu Novo Mundo"
            />
            <button
              className="secondary small"
              disabled={!newWorld || blocked}
              onClick={() => void addWorld()}
            >
              <Plus size={15} />
              Criar
            </button>
          </div>
          <div className="world-import">
            <label className="field">
              <span>Importar mundo existente</span>
              <input
                key={worldImportInputKey}
                type="file"
                accept=".db,.fwl"
                multiple
                onChange={(event) =>
                  setWorldImportFiles(Array.from(event.target.files ?? []))
                }
              />
              <small>Selecione o par .db + .fwl de uma só vez.</small>
            </label>
            <Field
              required={false}
              label="Nome no painel (opcional)"
              value={worldImportName}
              onChange={setWorldImportName}
              placeholder="usa o nome dos arquivos"
            />
            <label className="check world-import-activate">
              <input
                type="checkbox"
                checked={worldImportActivate}
                disabled={s.state !== "OFFLINE"}
                onChange={(event) =>
                  setWorldImportActivate(event.target.checked)
                }
              />
              <i />
              <span>
                <b>Ativar após importar</b>
                <small>
                  {s.state === "OFFLINE"
                    ? "Prepara o servidor para usar este mundo."
                    : "Desligue o servidor para ativar na importação."}
                </small>
              </span>
            </label>
            <label className="field">
              <span>Se o nome já existir</span>
              <select
                value={worldImportConflict}
                onChange={(event) => setWorldImportConflict(event.target.value)}
              >
                <option value="cancel">Cancelar sem alterar</option>
                <option value="keep-both">Manter os dois</option>
                <option value="replace">Substituir com backup</option>
              </select>
              <small>Nunca substituímos um mundo sem sua escolha.</small>
            </label>
            <button
              className="secondary small"
              disabled={worldImportFiles.length !== 2 || blocked}
              onClick={() => void importWorld()}
            >
              Importar par
            </button>
          </div>
          <div className="world-list">
            {worlds.map((w) => (
              <div className="world-row" key={w.id}>
                <span
                  className={
                    w.id === s.activeWorldId
                      ? "world-icon active"
                      : "world-icon"
                  }
                >
                  <Database size={18} />
                </span>
                <span>
                  <b>{w.name}</b>
                  <small>
                    {w.id === s.activeWorldId
                      ? "Em uso"
                      : w.archived
                        ? "Arquivado"
                        : "Guardado · não executa outro processo"}
                  </small>
                </span>
                <span className="world-size">{formatBytes(w.sizeBytes)}</span>
                <span className="world-actions">
                  {w.id !== s.activeWorldId && !w.archived && (
                    <button
                      className="text-button"
                      disabled={blocked}
                      onClick={() => void activate(w)}
                    >
                      Usar
                    </button>
                  )}
                  <a
                    className={
                      w.id === s.activeWorldId && s.state !== "OFFLINE"
                        ? "text-button disabled"
                        : "text-button"
                    }
                    aria-disabled={
                      w.id === s.activeWorldId && s.state !== "OFFLINE"
                    }
                    href={
                      w.id === s.activeWorldId && s.state !== "OFFLINE"
                        ? undefined
                        : `/api/v1/servers/${s.id}/worlds/${w.id}/export`
                    }
                  >
                    Baixar
                  </a>
                  <button
                    className="text-button"
                    disabled={blocked}
                    onClick={() => void cloneWorld(w)}
                  >
                    Duplicar
                  </button>
                  <button
                    className="text-button"
                    disabled={
                      blocked ||
                      (w.id === s.activeWorldId && s.state !== "OFFLINE")
                    }
                    onClick={() => void renameWorld(w)}
                  >
                    Renomear
                  </button>
                  {w.id !== s.activeWorldId && (
                    <button
                      className="text-button"
                      disabled={blocked}
                      onClick={() => void archiveWorld(w)}
                    >
                      {w.archived ? "Desarquivar" : "Arquivar"}
                    </button>
                  )}
                  {w.id !== s.activeWorldId && (
                    <button
                      className="text-button danger-text"
                      disabled={blocked}
                      onClick={() => void deleteWorld(w)}
                    >
                      Apagar
                    </button>
                  )}
                </span>
              </div>
            ))}
            {!worlds.length && <EmptyState kind="worlds" compact title="Nenhum mundo armazenado" description="Crie um mundo novo ou importe o par .db + .fwl existente." />}
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">BACKUPS</span>
              <h2>Pontos de restauração</h2>
            </div>
            <Archive size={20} />
          </div>
          {backups.length ? (
            <div className="backup-list">
              {backups.slice(0, 6).map((b) => (
                <div className="backup-row" key={b.id}>
                  <span>
                    <b>{backupName(b.kind)}</b>
                    <small>
                      {b.world || "Mundo"} · {relative(b.createdAt)} ·{" "}
                      {formatBytes(b.sizeBytes)}
                    </small>
                  </span>
                  <span className="backup-actions">
                    <a
                      className="text-button"
                      href={`/api/v1/servers/${s.id}/backups/${b.id}/download`}
                    >
                      Baixar
                    </a>
                    <button
                      className="text-button"
                      disabled={blocked}
                      onClick={() => void restore(b)}
                    >
                      Restaurar
                    </button>
                    <button
                      className="text-button danger-text"
                      disabled={blocked}
                      onClick={() => void removeBackup(b)}
                    >
                      Apagar
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState kind="backups" compact title="Nenhum backup ainda" description="Crie um backup manual ou configure a proteção automática do seu mundo." />
          )}
          {backupPolicy && (
            <details className="backup-policy">
              <summary>Retenção automática</summary>
              <p>
                Backups manuais não são removidos automaticamente. O último
                backup válido de cada mundo é sempre preservado.
              </p>
              <div className="backup-policy-grid">
                <label className="field">
                  <span>Automáticos por mundo</span>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    value={backupPolicy.automaticKeep}
                    onChange={(event) =>
                      setBackupPolicy({
                        ...backupPolicy,
                        automaticKeep: Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label className="field">
                  <span>Dias dos automáticos</span>
                  <input
                    type="number"
                    min="1"
                    max="3650"
                    value={backupPolicy.automaticDays}
                    onChange={(event) =>
                      setBackupPolicy({
                        ...backupPolicy,
                        automaticDays: Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label className="field">
                  <span>Antes de operações</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={backupPolicy.preOperationKeep}
                    onChange={(event) =>
                      setBackupPolicy({
                        ...backupPolicy,
                        preOperationKeep: Number(event.target.value),
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="secondary small"
                disabled={blocked}
                onClick={() => void saveBackupPolicy()}
              >
                Salvar retenção
              </button>
            </details>
          )}
        </section>
      </div>
      <section className="panel mods-panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">MODS</span>
            <h2>Adicionar mods ao seu servidor</h2>
          </div>
          <Boxes size={20} />
        </div>
        <div className="mod-support">
          <span>
            <b>Suporte a mods</b>
            <small>
              {installedMods.some(
                (item) => item.name === "BepInExPack_Valheim" && item.enabled,
              )
                ? "Pronto"
                : "Será preparado automaticamente no primeiro mod."}
            </small>
          </span>
          <i
            className={
              installedMods.some(
                (item) => item.name === "BepInExPack_Valheim" && item.enabled,
              )
                ? "status-dot online"
                : "status-dot"
            }
          />
        </div>
        {canManage && <div className="mod-search">
          <div className="search">
            <Search size={17} />
            <input
              value={modQuery}
              onChange={(event) => setModQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void searchMods();
              }}
              placeholder="Pesquisar no Thunderstore"
            />
          </div>
          <button
            className="secondary small"
            disabled={!modQuery.trim() || Boolean(busy)}
            onClick={() => void searchMods()}
          >
            Pesquisar
          </button>
        </div>}
        {canManage && modResults.length > 0 && (
          <div className="mod-results">
            {modResults.map((item) => (
              <article
                className="mod-card"
                key={`${item.id.namespace}-${item.id.name}`}
              >
                <span>
                  <b>{item.id.name}</b>
                  <small>por {item.id.namespace}</small>
                </span>
                <p>{item.description || "Sem descrição curta disponível."}</p>
                <small>
                  Versão {item.latestVersion}
                  {item.downloads > 0
                    ? ` · ${new Intl.NumberFormat("pt-BR").format(item.downloads)} downloads`
                    : ""}
                </small>
                <button
                  className="secondary small"
                  disabled={blocked}
                  onClick={() => void previewMod(item)}
                >
                  {installedMods.some(
                    (installed) =>
                      installed.namespace === item.id.namespace &&
                      installed.name === item.id.name,
                  )
                    ? "Ver atualização"
                    : "Instalar"}
                </button>
              </article>
            ))}
          </div>
        )}
        {canManage && modPlan && modSelection && (
          <div className="mod-preview">
            <div>
              <b>Serão preparados {modPlan.operations.length} componentes</b>
              <small>
                Dependências são incluídas automaticamente e o mundo recebe um
                backup antes da instalação.
              </small>
            </div>
            <ul>
              {modPlan.operations.map((operation) => (
                <li key={`${operation.package.namespace}-${operation.package.name}`}>
                  <span>
                    {operation.package.name}
                    <small>por {operation.package.namespace}</small>
                  </span>
                  <b>{operation.to}</b>
                </li>
              ))}
            </ul>
            <div className="inline-actions">
              <button
                className="primary small"
                disabled={blocked}
                onClick={() => void installModPlan()}
              >
                Instalar tudo
              </button>
              <button
                className="text-button"
                onClick={() => {
                  setModPlan(null);
                  setModSelection(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
        {installedMods.length > 0 && (
          <div className="installed-mods">
            <div className="panel-head">
              <h3>Instalados</h3>
              {canManage && (
                <button className="secondary small" disabled={blocked} onClick={() => void previewAllModUpdates()}>
                  Atualizar todos
                </button>
              )}
            </div>
            {modUpdateAll && (
              <div className="mod-preview">
                <div>
                  <b>{modUpdateAll.changes === 0 ? "Tudo atualizado" : `${modUpdateAll.updates} mods serão atualizados`}</b>
                  <small>
                    {modUpdateAll.dependencies} dependências alteradas · {modUpdateAll.pinned} versões fixadas mantidas · {modUpdateAll.disabled} desativados preservados
                  </small>
                </div>
                {modUpdateAll.changes > 0 && (
                  <ul>
                    {modUpdateAll.plan.operations.filter((operation) => operation.action !== "keep").map((operation) => (
                      <li key={`${operation.package.namespace}-${operation.package.name}`}>
                        <span>{operation.package.name}</span>
                        <b>{operation.from} → {operation.to}</b>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="inline-actions">
                  {modUpdateAll.changes > 0 && (
                    <button className="primary small" disabled={blocked} onClick={() => void applyAllModUpdates()}>
                      Aplicar atualização segura
                    </button>
                  )}
                  <button className="text-button" onClick={() => setModUpdateAll(null)}>Fechar</button>
                </div>
              </div>
            )}
            {modUpdate && (
              <div className="mod-preview">
                <div>
                  <b>{modUpdate.item.name}</b>
                  <small>
                    {modUpdate.preview.upToDate
                      ? `A versão ${modUpdate.preview.current} já é a mais recente.`
                      : `${modUpdate.preview.current} → ${modUpdate.preview.latest} · ${modUpdate.preview.plan.operations.filter((operation) => operation.action !== "keep" && operation.package.name !== modUpdate.item.name).length} dependências alteradas`}
                  </small>
                </div>
                {!modUpdate.preview.upToDate && (
                  <ul>
                    {modUpdate.preview.plan.operations.filter((operation) => operation.action !== "keep").map((operation) => (
                      <li key={`${operation.package.namespace}-${operation.package.name}`}>
                        <span>{operation.package.name}</span>
                        <b>{operation.from} → {operation.to}</b>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="inline-actions">
                  {!modUpdate.preview.upToDate && (
                    <button className="primary small" disabled={blocked} onClick={() => void applyInstalledModUpdate()}>
                      Atualizar com backup
                    </button>
                  )}
                  <button className="text-button" onClick={() => setModUpdate(null)}>Fechar</button>
                </div>
              </div>
            )}
            {installedMods.map((item) => (
              <div className="installed-mod" key={`${item.namespace}-${item.name}`}>
                <span>
                  <b>{item.name}</b>
                  <small>
                    {item.namespace} · versão {item.version} · {item.enabled ? "ativo" : "desativado"}
                  </small>
                </span>
                <span className={`compatibility ${item.compatibility}`}>
                  {item.compatibility === "compatible"
                    ? `Validado no build ${item.lastValidatedBuild}`
                    : item.compatibility === "incompatible"
                      ? `Falha detectada no build ${item.lastValidatedBuild}`
                      : "Compatibilidade desconhecida"}
                </span>
                {item.lastError && <small title={item.lastError}>Há um erro deste mod registrado nos logs.</small>}
                {canManage && <div className="inline-actions">
                  <button
                    className="secondary small"
                    disabled={blocked || item.pinned || !item.enabled}
                    onClick={() => void previewInstalledModUpdate(item)}
                  >
                    Atualizar
                  </button>
                  <button
                    className="text-button"
                    disabled={blocked}
                    onClick={() => void setModPinned(item)}
                  >
                    {item.pinned ? "Desafixar versão" : "Fixar versão"}
                  </button>
                  {item.name !== "BepInExPack_Valheim" && (
                    <button
                      className="secondary small"
                      disabled={blocked}
                      onClick={() => void setModEnabled(item)}
                    >
                      {item.enabled ? "Desativar" : "Ativar"}
                    </button>
                  )}
                  {item.name !== "BepInExPack_Valheim" && (
                    <button
                      className="text-button"
                      disabled={blocked}
                      onClick={() => void removeInstalledMod(item)}
                    >
                      Remover
                    </button>
                  )}
                </div>}
              </div>
            ))}
          </div>
        )}
        {modProfiles.length > 0 && (
          <details className="advanced-box">
            <summary>Perfis anteriores</summary>
            <div className="installed-mods">
              {modProfiles.map((profile) => (
                <div className="installed-mod" key={profile.id}>
                  <span>
                    <b>{profile.packages} componentes</b>
                    <small>{profile.reason} · {relative(profile.createdAt)}</small>
                  </span>
                  {canManage && (
                    <button
                      className="secondary small"
                      disabled={blocked}
                      onClick={() => void restoreModProfile(profile)}
                    >
                      Restaurar
                    </button>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
        {canManage && installedMods.length > 0 && (
          <details className="advanced-box">
            <summary>Configurações avançadas dos mods</summary>
            <p>Somente configurações existentes podem ser editadas. Cada alteração preserva uma versão anterior e entra em vigor no próximo início.</p>
            <button className="secondary small" disabled={blocked} onClick={() => void loadModConfigs()}>
              Carregar configurações
            </button>
            {modConfigs.map((item) => (
              <button
                className="text-button"
                key={item.name}
                disabled={blocked}
                onClick={() => void openModConfig(item)}
              >
                {item.name} · {formatBytes(item.sizeBytes)}
              </button>
            ))}
            {selectedModConfig && (
              <div className="config-editor">
                <b>{selectedModConfig.name}</b>
                {(selectedModConfig.fields ?? []).length > 0 && (
                  <div className="config-fields">
                    {selectedModConfig.fields?.map((field) => (
                      <label className="field" key={`${field.section}-${field.key}-${field.line}`}>
                        <span>{field.key}<small>{field.section}{field.description ? ` · ${field.description}` : ""}</small></span>
                        {field.kind === "bool" ? (
                          <select value={field.value.toLowerCase()} onChange={(event) => updateModConfigField(field, event.target.value)}>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                          </select>
                        ) : field.kind === "enum" && (field.options ?? []).length > 0 ? (
                          <select value={field.value} onChange={(event) => updateModConfigField(field, event.target.value)}>
                            {field.options?.map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                        ) : (
                          <input
                            type={field.kind === "int" || field.kind === "float" ? "number" : "text"}
                            step={field.kind === "float" ? "any" : undefined}
                            value={field.value}
                            onChange={(event) => updateModConfigField(field, event.target.value)}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                )}
                <details>
                  <summary>Editor avançado</summary>
                  <textarea
                    rows={16}
                    value={selectedModConfig.content ?? ""}
                    onChange={(event) => setSelectedModConfig({ ...selectedModConfig, content: event.target.value, fields: [] })}
                    spellCheck={false}
                  />
                </details>
                <div className="inline-actions">
                  <button className="primary small" disabled={blocked} onClick={() => void saveModConfig()}>
                    Salvar configuração
                  </button>
                  <button className="text-button" onClick={() => setSelectedModConfig(null)}>Fechar</button>
                </div>
                {modConfigRevisions.length > 0 && (
                  <details>
                    <summary>Versões anteriores</summary>
                    {modConfigRevisions.map((revision) => (
                      <button className="text-button" disabled={blocked} key={revision.id} onClick={() => void restoreModConfig(revision)}>
                        {relative(revision.createdAt)} · {formatBytes(revision.sizeBytes)}
                      </button>
                    ))}
                  </details>
                )}
              </div>
            )}
          </details>
        )}
      </section>
      {gameplay && (
        <section className="panel gameplay-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">GAMEPLAY OFICIAL</span>
              <h2>Regras do mundo {gameplay.worldName}</h2>
            </div>
            <Database size={20} />
          </div>
          <p>
            O preset define a base. Os modificadores abaixo substituem somente a
            regra escolhida e são aplicados no próximo início.
          </p>
          <div className="gameplay-body">
            <h3>Preset</h3>
            <div className="preset-row gameplay-presets">
              {[
                ["normal", "Normal"],
                ["casual", "Casual"],
                ["easy", "Fácil"],
                ["hard", "Difícil"],
                ["hardcore", "Hardcore"],
                ["immersive", "Imersivo"],
                ["hammer", "Construção"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  className={
                    gameplay.preset === value ? "preset active" : "preset"
                  }
                  disabled={blocked}
                  onClick={() => setGameplay({ ...gameplay, preset: value })}
                >
                  {label}
                </button>
              ))}
            </div>
            <h3>Ajustes específicos</h3>
            <div className="gameplay-selects">
              <GameplaySelect
                label="Combate"
                value={gameplay.modifiers.combat || ""}
                onChange={(v) => setModifier("combat", v)}
                options={[
                  ["veryeasy", "Muito fácil"],
                  ["easy", "Fácil"],
                  ["hard", "Difícil"],
                  ["veryhard", "Muito difícil"],
                ]}
              />
              <GameplaySelect
                label="Penalidade de morte"
                value={gameplay.modifiers.deathpenalty || ""}
                onChange={(v) => setModifier("deathpenalty", v)}
                options={[
                  ["casual", "Casual"],
                  ["veryeasy", "Muito leve"],
                  ["easy", "Leve"],
                  ["hard", "Pesada"],
                  ["hardcore", "Hardcore"],
                ]}
              />
              <GameplaySelect
                label="Recursos"
                value={gameplay.modifiers.resources || ""}
                onChange={(v) => setModifier("resources", v)}
                options={[
                  ["muchless", "Muito menos"],
                  ["less", "Menos"],
                  ["more", "Mais"],
                  ["muchmore", "Muito mais"],
                  ["most", "Máximo"],
                ]}
              />
              <GameplaySelect
                label="Incursões"
                value={gameplay.modifiers.raids || ""}
                onChange={(v) => setModifier("raids", v)}
                options={[
                  ["none", "Nenhuma"],
                  ["muchless", "Muito menos"],
                  ["less", "Menos"],
                  ["more", "Mais"],
                  ["muchmore", "Muito mais"],
                ]}
              />
              <GameplaySelect
                label="Portais"
                value={gameplay.modifiers.portals || ""}
                onChange={(v) => setModifier("portals", v)}
                options={[
                  ["casual", "Permissivo"],
                  ["hard", "Restrito"],
                  ["veryhard", "Muito restrito"],
                ]}
              />
            </div>
            <h3>Regras adicionais</h3>
            <div className="gameplay-keys">
              {[
                [
                  "nobuildcost",
                  "Construção sem custo",
                  "Construções não consomem materiais.",
                ],
                [
                  "playerevents",
                  "Eventos por jogador",
                  "O progresso individual influencia os eventos.",
                ],
                [
                  "passivemobs",
                  "Criaturas passivas",
                  "Criaturas não atacam até serem provocadas.",
                ],
                ["nomap", "Sem mapa", "Desativa o mapa durante a exploração."],
              ].map(([key, label, help]) => (
                <label className="check" key={key}>
                  <input
                    type="checkbox"
                    checked={gameplay.worldKeys.includes(key)}
                    disabled={blocked}
                    onChange={() => toggleWorldKey(key)}
                  />
                  <i />
                  <span>
                    <b>{label}</b>
                    <small>{help}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="inline-actions">
            <button
              className="primary small"
              disabled={blocked}
              onClick={() => void saveGameplay(false)}
            >
              {s.state === "ONLINE"
                ? "Salvar para o próximo início"
                : "Salvar regras"}
            </button>
            {s.state === "ONLINE" && (
              <button
                className="secondary small"
                disabled={blocked}
                onClick={() => void saveGameplay(true)}
              >
                Salvar e reiniciar
              </button>
            )}
          </div>
        </section>
      )}
      <section className="panel server-settings-panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">CONFIGURAÇÕES DO SERVIDOR</span>
            <h2>Identidade, acesso e execução</h2>
          </div>
          <ServerIcon size={20} />
        </div>
        <p>
          A senha atual nunca é exibida. Deixe “Nova senha” vazio para mantê-la.
        </p>
        <div className="settings-form">
          <div className="field-row">
            <Field
              label="Nome visível"
              value={serverDraft.displayName}
              onChange={(displayName) =>
                setServerDraft({ ...serverDraft, displayName })
              }
            />
            <Field
              required={false}
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              value={serverDraft.newPassword}
              onChange={(newPassword) =>
                setServerDraft({ ...serverDraft, newPassword })
              }
              placeholder="Manter senha atual"
            />
          </div>
          <div className="settings-checks">
            <label className="check">
              <input
                type="checkbox"
                checked={serverDraft.public}
                onChange={(e) =>
                  setServerDraft({ ...serverDraft, public: e.target.checked })
                }
              />
              <i />
              <span>
                <b>Lista pública</b>
                <small>Permite que o Valheim anuncie este servidor.</small>
              </span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={serverDraft.crossplay}
                onChange={(e) =>
                  setServerDraft({
                    ...serverDraft,
                    crossplay: e.target.checked,
                  })
                }
              />
              <i />
              <span>
                <b>Crossplay</b>
                <small>Usa o relay para jogadores de outras plataformas.</small>
              </span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={serverDraft.autostart}
                onChange={(e) =>
                  setServerDraft({
                    ...serverDraft,
                    autostart: e.target.checked,
                  })
                }
              />
              <i />
              <span>
                <b>Iniciar com o host</b>
                <small>Liga este servidor quando o serviço iniciar.</small>
              </span>
            </label>
          </div>
          <details className="settings-advanced">
            <summary>Opções avançadas</summary>
            <p>
              Intervalos são informados em segundos. Limites de memória usam
              bytes; zero mantém o limite desativado. Em CPU, 100% equivale a um
              núcleo.
            </p>
            <div className="advanced-settings-grid">
              <Field
                label="Porta base"
                type="number"
                value={serverDraft.port}
                onChange={(port) => setServerDraft({ ...serverDraft, port })}
              />
              <Field
                label="Salvamento"
                type="number"
                value={serverDraft.saveInterval}
                onChange={(saveInterval) =>
                  setServerDraft({ ...serverDraft, saveInterval })
                }
              />
              <Field
                label="Backups nativos"
                type="number"
                value={serverDraft.backups}
                onChange={(backups) =>
                  setServerDraft({ ...serverDraft, backups })
                }
              />
              <Field
                label="Backup curto"
                type="number"
                value={serverDraft.backupShort}
                onChange={(backupShort) =>
                  setServerDraft({ ...serverDraft, backupShort })
                }
              />
              <Field
                label="Backup longo"
                type="number"
                value={serverDraft.backupLong}
                onChange={(backupLong) =>
                  setServerDraft({ ...serverDraft, backupLong })
                }
              />
              <Field
                label="Canal Steam"
                value={serverDraft.steamBranch}
                onChange={(steamBranch) =>
                  setServerDraft({ ...serverDraft, steamBranch })
                }
              />
              <Field
                label="Aviso de memória"
                type="number"
                value={serverDraft.memoryHigh}
                onChange={(memoryHigh) =>
                  setServerDraft({ ...serverDraft, memoryHigh })
                }
              />
              <Field
                label="Limite de memória"
                type="number"
                value={serverDraft.memoryMax}
                onChange={(memoryMax) =>
                  setServerDraft({ ...serverDraft, memoryMax })
                }
              />
              <Field
                label="Limite de CPU (%)"
                type="number"
                value={serverDraft.cpuQuota}
                onChange={(cpuQuota) =>
                  setServerDraft({ ...serverDraft, cpuQuota })
                }
              />
              <Field
                label="Prioridade do processo"
                type="number"
                value={serverDraft.nice}
                onChange={(nice) => setServerDraft({ ...serverDraft, nice })}
              />
            </div>
          </details>
        </div>
        <div className="inline-actions">
          <button
            className="primary small"
            disabled={blocked}
            onClick={() => void saveServerSettings(false)}
          >
            {s.state === "ONLINE"
              ? "Salvar para o próximo início"
              : "Salvar configurações"}
          </button>
          {s.state === "ONLINE" && (
            <button
              className="secondary small"
              disabled={blocked}
              onClick={() => void saveServerSettings(true)}
            >
              Salvar e reiniciar agora
            </button>
          )}
        </div>
      </section>
      <div className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">ECONOMIZAR RECURSOS</span>
              <h2>Desligar quando estiver vazio</h2>
            </div>
          </div>
          <p>
            O contador só começa depois que o Valheim confirma que não há
            ninguém jogando. Qualquer entrada cancela a contagem.
          </p>
          <div className="preset-row">
            {[
              [0, "Desativado"],
              [15, "15 min"],
              [30, "30 min"],
              [60, "1 hora"],
            ].map(([value, label]) => (
              <button
                key={value}
                className={ecoStop === value ? "preset active" : "preset"}
                onClick={() => setEcoStop(Number(value))}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="inline-actions">
            <button
              className="primary small"
              disabled={blocked || ecoStop === s.ecoStopMinutes}
              onClick={() => void saveEconomy()}
            >
              Salvar
            </button>
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">AUTOMAÇÕES</span>
              <h2>Tarefas recorrentes</h2>
            </div>
            <CalendarClock size={20} />
          </div>
          <div className="schedule-list">
            {schedules.map((sc) => (
              <div className="schedule-row" key={sc.id}>
                <span>
                  <b>{scheduleName(sc.template)}</b>
                  <small>Próxima: {relative(sc.nextRunAt)}</small>
                </span>
                <button
                  className="icon-btn"
                  disabled={blocked}
                  onClick={() => void removeSchedule(sc.id)}
                  aria-label="Remover automação"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {!schedules.length && <p>Nenhuma automação configurada.</p>}
          </div>
          <div className="inline-actions">
            {!schedules.some((x) => x.template === "backup-2h") && (
              <button
                className="secondary small"
                disabled={blocked}
                onClick={() => void addSchedule("backup-2h")}
              >
                Backup a cada 2 horas
              </button>
            )}
            {!schedules.some((x) => x.template === "restart-daily-05") && (
              <button
                className="secondary small"
                disabled={blocked}
                onClick={() => void addSchedule("restart-daily-05")}
              >
                Reiniciar às 05:00
              </button>
            )}
          </div>
        </section>
      </div>
      <div className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">JOGADORES</span>
              <h2>Online agora</h2>
            </div>
          </div>
          <div className="access-list">
            {players
              .filter((p) => p.online)
              .map((p) => (
                <div className="access-row" key={p.id}>
                  <Avatar name={p.characterName || p.platformId || "Jogador"} />
                  <span>
                    <b>{p.characterName || "Jogador conectado"}</b>
                    <small>
                      {p.platformId || "Identidade de plataforma indisponível"}
                    </small>
                    <small>{p.gameAdmin ? "Admin do jogo ✓" : "Jogador"} · {p.clientBridge?.connected ? `Client Bridge ${p.clientBridge.version ?? "conectado"}` : "Cliente padrão"}</small>
                    <small>Tempo total registrado: {formatPlayTime(p.totalPlayedSeconds)}</small>
                  </span>
                </div>
              ))}
            {!players.some((p) => p.online) && (
              <div className="empty-inline">
                Nenhum jogador confirmado online.
              </div>
            )}
          </div>
          <details>
            <summary>Histórico recente</summary>
            <div className="access-list">
              {players
                .filter((p) => !p.online)
                .slice(0, 20)
                .map((p) => (
                  <div className="access-row" key={p.id}>
                    <span>
                      <b>{p.characterName || "Jogador conectado"}</b>
                      <small>
                        {p.platformId || "ID desconhecido"} ·{" "}
                        {relative(p.connectedAt)} · {formatPlayTime(p.totalPlayedSeconds)} no servidor
                      </small>
                    </span>
                  </div>
                ))}
            </div>
          </details>
        </section>
        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">ACESSO</span>
              <h2>Admins, banidos e permitidos</h2>
            </div>
          </div>
          <p>
            Use o ID de plataforma exibido pelo Valheim. Estas listas são
            aplicadas aos arquivos oficiais do servidor.
          </p>
          <div className="access-form">
            <select
              value={accessDraft.type}
              onChange={(e) =>
                setAccessDraft({
                  ...accessDraft,
                  type: e.target.value as AccessEntry["type"],
                })
              }
            >
              <option value="admin">Administrador</option>
              <option value="permitted">Permitido</option>
              <option value="banned">Banido</option>
            </select>
            <input
              value={accessDraft.platformId}
              onChange={(e) =>
                setAccessDraft({ ...accessDraft, platformId: e.target.value })
              }
              placeholder="ID da plataforma"
            />
            <input
              value={accessDraft.note}
              onChange={(e) =>
                setAccessDraft({ ...accessDraft, note: e.target.value })
              }
              placeholder="Nome ou observação (opcional)"
            />
            <button
              className="secondary small"
              disabled={blocked || !accessDraft.platformId}
              onClick={() => void addAccess()}
            >
              Adicionar
            </button>
          </div>
          <div className="access-list">
            {access.map((item) => (
              <div
                className="access-row"
                key={`${item.type}-${item.platformId}`}
              >
                <span>
                  <b>{item.note || item.platformId}</b>
                  <small>
                    {accessName(item.type)} · {item.note && item.platformId}
                  </small>
                </span>
                <button
                  className="icon-btn"
                  disabled={blocked}
                  onClick={() => void removeAccess(item)}
                  aria-label="Remover da lista"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {!access.length && (
              <div className="empty-inline">
                Nenhuma regra de acesso personalizada.
              </div>
            )}
          </div>
        </section>
      </div>
      <section className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">OPÇÕES ATUAIS</span>
            <h2>Configuração efetiva</h2>
          </div>
        </div>
        <dl className="config-list">
          <Config
            label="Salvamento"
            value={`a cada ${Math.round(s.saveInterval / 60)} min`}
          />
          <Config label="Backups nativos" value={String(s.backups)} />
          <Config
            label="Início automático"
            value={s.autostart ? "Ativado" : "Desativado"}
          />
          <Config
            label="Limite de memória"
            value={
              s.memoryMax ? formatBytes(s.memoryMax) : "Sem limite adicional"
            }
          />
        </dl>
      </section>
      {canManage && (
        <section className="panel delete-server-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">ZONA DE PERIGO</span>
              <h2>Excluir servidor</h2>
            </div>
          </div>
          <p>
            Esta ação remove o servidor e seus arquivos deste host. Os backups
            locais também serão removidos. Crie e baixe um backup antes de
            continuar.
          </p>
          <label>
            Digite <b>{s.displayName}</b> para confirmar
            <input
              aria-label="Nome do servidor para confirmar exclusão"
              value={deleteConfirmName}
              onChange={(event) => setDeleteConfirmName(event.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={deleteBackupsAcknowledged}
              onChange={(event) =>
                setDeleteBackupsAcknowledged(event.target.checked)
              }
            />
            Entendo que os backups locais deste servidor serão excluídos.
          </label>
          <button
            className="danger"
            disabled={
              blocked ||
              deleteConfirmName !== s.displayName ||
              !deleteBackupsAcknowledged
            }
            onClick={() => void deleteServer()}
          >
            <Trash2 size={16} />
            {busy === "server-delete" ? "Excluindo…" : "Excluir servidor"}
          </button>
        </section>
      )}
      <FileManager
        serverId={s.id}
        role={fileRole ?? (canManage ? "administrator" : "viewer")}
      />
      {dialogHost}
    </>
  );
}
function draftFromServer(s: Server): ServerSettingsDraft {
  return {
    displayName: s.displayName,
    newPassword: "",
    public: s.public,
    crossplay: s.crossplay,
    autostart: s.autostart,
    port: String(s.port),
    saveInterval: String(s.saveInterval),
    backups: String(s.backups),
    backupShort: String(s.backupShort),
    backupLong: String(s.backupLong),
    steamBranch: s.steamBranch,
    memoryHigh: String(s.memoryHigh),
    memoryMax: String(s.memoryMax),
    cpuQuota: String(s.cpuQuota),
    nice: String(s.nice),
  };
}
function GameplaySelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly (readonly [string, string])[];
}) {
  return (
    <label className="field gameplay-select">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Usar o preset</option>
        {options.map(([key, name]) => (
          <option value={key} key={key}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}
const backupName = (kind: string) =>
  ({
    manual: "Backup manual",
    "pre-update": "Antes da atualização",
    "pre-world-switch": "Antes da troca de mundo",
    "pre-restore": "Antes da restauração",
  })[kind] ?? "Backup";
const scheduleName = (template: string) =>
  template === "backup-2h"
    ? "Backup a cada 2 horas"
    : "Reinício diário às 05:00";
const accessName = (type: AccessEntry["type"]) =>
  ({ admin: "Administrador", banned: "Banido", permitted: "Permitido" })[type];
const formatPlayTime = (seconds: number) => {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}min` : `${minutes}min`;
};
